import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Grid, TextField, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import SimpleBackdrop from '../../Loaders/SimpleBackdrop'
import FacebookLoginButton from '../../Base/FacebookLoginButton'
import GoogleLoginButton from '../../Base/GoogleLoginButton'
import Alert from '@material-ui/lab/Alert'
import { getErrorMessage, inputHasError } from '../../../utils/formErrorUtils'
import { emailSchema } from '../../formValidationSchemas'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'

const useStyles = makeStyles((theme) => ({
    root: {
        [theme.breakpoints.up('md')]: {
            paddingRight: 20,
        },
    },
    hr: {
        color: '#8f8f8f',
    },
    loginBtn: {
        margin: '0.5rem 0 1.1rem 0',
        padding: '0.4rem',
        fontSize: '1rem',
        textTransform: 'none',
        fontWeight: 'bold',
    },
    socialLoginButtonContainer: {
        flexDirection: 'column',
        margin: '1.15rem 0',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
        },
    },
    facebookButton: {
        marginBottom: '1rem',
    },
    loginButton: {
        marginBottom: '0.5rem',
        padding: '0.6rem',
        fontSize: '1.125rem',
        textTransform: 'none',
    },
    buttonIcon: {
        fontSize: '1rem',
        marginLeft: '.15rem',
    },
    forgotPasswordLink: {
        flex: 1,
        margin: '0.5rem 0',
        fontSize: '0.9375rem',
        color: theme.palette.primary.main,
        textDecoration: 'none',
    },
    termsText: {
        textAlign: 'left',
        fontSize: '0.8rem',
        color: theme.palette.secondary.main,
        margin: '1rem auto',
        maxWidth: 246,
        [theme.breakpoints.up('md')]: {
            maxWidth: 'none',
        },
        '& a': {
            color: theme.palette.secondary.main,
            textDecoration: 'underline',
        },
    },
    linkText: {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
    },
    roundedBtn: {
        borderRadius: 50,
        borderWidth: 3,
        '&:hover': {
            borderRadius: 50,
            borderWidth: 3,
        },
    },
    greenBtn: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.secondary.main,
        '&:hover': {
            borderColor: theme.palette.secondary.main,
        },
    },
    greyRoundTextinput: {
        width: '100%',
        maxWidth: '100%',
        backgroundColor: '#F4F4F4',
        borderRadius: '60px',
        height: '42px',
        padding: '0 0 2rem 0',
        margin: '0 0 28px 0',
        '& fieldset': {
            borderRadius: '60px',
        },
        '& input': {
            padding: '11.5px 14px',
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: '60px',
        },
    },
    seperator: {
        marginBottom: '1rem',
    },
    seperatorText: {
        fontSize: '1rem',
        fontWeight: 'bold',
        textAlign: 'center',
    },
}))

const ConsumerCheckoutLoginModalContent = (props) => {
    const {
        baseUrl,
        facebookLoginUrl,
        googleLoginUrl,
        loginDetails,
        setLoginDetails,
        loginFormErrors,
        processing,
        errors,
        handleShowGuestCheckout,
        guestEmail,
        setGuestEmail,
        handleShowCreateAccount,
        validateLoginField,
        handleUserLogin,
    } = props
    const classes = useStyles()

    useEffect(() => {
        const listener = (event) => {
            if (event.code === 'Enter' || event.code === 'NumpadEnter') {
                event.preventDefault()
                handleUserLogin(null)
            }
        }
        document.addEventListener('keydown', listener)
        return () => {
            document.removeEventListener('keydown', listener)
        }
    }, [handleUserLogin])

    return (
        <React.Fragment>
            <SimpleBackdrop open={processing} />
            <Box>
                <Grid>
                    <TextField
                        style={{ marginTop: '2rem' }}
                        id={'email-address'}
                        variant={'outlined'}
                        placeholder={'Email address'}
                        value={loginDetails.email}
                        error={inputHasError(loginFormErrors, 'email')}
                        helperText={getErrorMessage(loginFormErrors, 'email')}
                        disabled={processing}
                        className={classes.greyRoundTextinput}
                        onChange={(e) => {
                            const email = e.target.value.trim()
                            validateLoginField('email', email)
                            setLoginDetails({
                                ...loginDetails,
                                email,
                            })
                        }}
                        inputProps={{
                            'data-testid': 'email',
                        }}
                    />
                    <TextField
                        id={'password'}
                        variant={'outlined'}
                        placeholder={'Password'}
                        type={'password'}
                        value={loginDetails.password}
                        error={inputHasError(loginFormErrors, 'password')}
                        className={classes.greyRoundTextinput}
                        helperText={getErrorMessage(
                            loginFormErrors,
                            'password'
                        )}
                        disabled={processing}
                        onChange={(e) => {
                            validateLoginField('password', e.target.value)
                            setLoginDetails({
                                ...loginDetails,
                                password: e.target.value,
                            })
                        }}
                        inputProps={{
                            'data-testid': 'password',
                        }}
                    />
                    <Button
                        id="login-button"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="large"
                        className={`${classes.roundedBtn} ${classes.greenBtn} ${classes.loginBtn}`}
                        disabled={processing}
                        onClick={() => {
                            handleUserLogin(null)
                        }}
                        data-testid="login-button"
                    >
                        Log In{' '}
                        <LockOutlinedIcon className={classes.buttonIcon} />
                    </Button>
                    {errors.length > 0 && (
                        <Alert
                            severity="error"
                            style={{ margin: '1rem 0 1.5rem 0' }}
                        >
                            {errors.map((error, index) => {
                                return (
                                    <div
                                        key={index}
                                        style={{ textWrap: 'pretty' }}
                                    >
                                        {error}
                                    </div>
                                )
                            })}
                        </Alert>
                    )}
                    <Box textAlign={'center'}>
                        <a
                            href={`${baseUrl}/secure/forgot-my-password/`}
                            className={classes.forgotPasswordLink}
                        >
                            <strong>Forgot password?</strong>
                        </a>
                        <Typography gutterBottom className={classes.termsText}>
                            This site is protected by reCAPTCHA and the Google{' '}
                            <a
                                className={classes.linkText}
                                href={'https://policies.google.com/privacy'}
                                target="_blank"
                            >
                                Privacy Policy
                            </a>{' '}
                            and{' '}
                            <a
                                className={classes.linkText}
                                href={'https://policies.google.com/terms'}
                                target="_blank"
                            >
                                Terms of Service
                            </a>{' '}
                            apply.
                        </Typography>
                    </Box>

                    <Box
                        className={classes.seperator}
                        display={'flex'}
                        alignItems={'center'}
                    >
                        <hr
                            className={classes.hr}
                            style={{ flex: 1, marginRight: '1rem' }}
                        />
                        <Typography className={classes.seperatorText}>
                            Or sign in with
                            <br /> social networks
                        </Typography>
                        <hr
                            className={classes.hr}
                            style={{ flex: 1, marginLeft: '1rem' }}
                        />
                    </Box>
                    <Grid
                        container
                        spacing={2}
                        className={classes.socialLoginButtonContainer}
                    >
                        <Grid item sm={12} md={6}>
                            <FacebookLoginButton
                                facebookLoginUrl={facebookLoginUrl}
                                classNameOverride={`${classes.facebookButton} ${classes.roundedBtn}`}
                            />
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <GoogleLoginButton
                                googleLoginUrl={googleLoginUrl}
                                classNameOverride={`${classes.roundedBtn}`}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </React.Fragment>
    )
}

export default ConsumerCheckoutLoginModalContent
